<div class="flex h-full flex-col">

  <!-- Page header -->
  <div class="px-6 pt-6 pb-4 lg:px-8">
    <h1 class="text-xl font-semibold text-slate-900">GitHub Issues</h1>
    <p class="mt-1 text-sm text-slate-500">Monitoring-linked GitHub issues created by KernalHQ for your connected resources.</p>
  </div>

  <!-- Loading connection -->
  <div *ngIf="state === 'loading'" class="flex items-center gap-2 px-6 py-10 text-sm text-slate-400">
    <lucide-icon name="loader" [size]="16" class="animate-spin" [strokeWidth]="2"></lucide-icon>
    Loading...
  </div>

  <!-- Not connected -->
  <div *ngIf="state === 'not-connected'" class="mx-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
    <div class="flex flex-col items-center gap-4 px-8 py-16 text-center">
      <div class="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100">
        <lucide-icon name="github" [size]="28" class="text-slate-400" [strokeWidth]="1.5"></lucide-icon>
      </div>
      <div>
        <h3 class="text-sm font-semibold text-slate-900">No GitHub connection</h3>
        <p class="mt-1.5 max-w-xs text-sm text-slate-500">
          Connect your GitHub account in Integration Settings to enable monitoring-linked issue tracking.
        </p>
      </div>
      <a
        routerLink="/settings/integrations"
        class="mt-1 flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
      >
        <lucide-icon name="settings" [size]="14" [strokeWidth]="2"></lucide-icon>
        Go to Integration Settings
      </a>
    </div>
  </div>

  <!-- Generic connection error -->
  <div *ngIf="state === 'error'" class="mx-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
    <lucide-icon name="triangle-alert" [size]="15" class="mt-0.5 flex-shrink-0 text-red-500" [strokeWidth]="2"></lucide-icon>
    <p class="text-sm text-red-700">Failed to load GitHub connection. Check your connection and try again.</p>
  </div>

  <!-- Connected workspace -->
  <ng-container *ngIf="state === 'connected' && connection">

    <!-- Connection context bar -->
    <div class="mx-6 mb-5 flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-2.5 shadow-sm">
      <div class="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded bg-slate-900">
        <lucide-icon name="github" [size]="13" color="white" [strokeWidth]="2"></lucide-icon>
      </div>
      <span class="text-sm font-medium text-slate-900">{{ connection.githubLogin }}</span>
      <span class="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Connected</span>
      <a
        routerLink="/settings/integrations"
        class="ml-auto text-xs font-medium text-slate-400 transition-colors hover:text-slate-700"
      >Manage</a>
    </div>

    <!-- Filter bar -->
    <div class="mx-6 mb-4 flex flex-wrap items-center gap-3">
      <!-- Search -->
      <div class="relative flex-1">
        <lucide-icon name="search" [size]="14" class="absolute top-1/2 -translate-y-1/2 text-slate-400" style="left: 0.5em" [strokeWidth]="2"></lucide-icon>
        <input
          type="text"
          [(ngModel)]="query"
          (ngModelChange)="onQueryChange()"
          placeholder="Search by repository, issue # or resource…"
          class="w-full rounded-lg border border-slate-200 bg-white py-2 pl-6 pr-3 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100"
        />
      </div>
      <!-- Status filter -->
      <select
        [(ngModel)]="statusFilter"
        (ngModelChange)="onStatusChange()"
        class="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100"
      >
        <option value="ALL">All statuses</option>
        <option value="ACTIVE">Open</option>
        <option value="RESOLVED">Resolved</option>
      </select>
      <!-- Refresh -->
      <button
        (click)="loadTickets()"
        [disabled]="ticketsLoading"
        class="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50"
      >
        <lucide-icon name="refresh-cw" [size]="13" [class.animate-spin]="ticketsLoading" [strokeWidth]="2"></lucide-icon>
        Refresh
      </button>
    </div>

    <!-- Tickets error -->
    <div *ngIf="ticketsError" class="mx-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
      <lucide-icon name="triangle-alert" [size]="15" class="mt-0.5 flex-shrink-0 text-red-500" [strokeWidth]="2"></lucide-icon>
      <p class="text-sm text-red-700">Failed to load linked GitHub issues. <button (click)="loadTickets()" class="font-medium underline">Try again</button></p>
    </div>

    <!-- Skeleton loading -->
    <div *ngIf="ticketsLoading" class="mx-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div class="divide-y divide-slate-100">
        <div *ngFor="let _ of [1,2,3,4,5]" class="flex items-center gap-4 px-5 py-4">
          <div class="h-3 w-16 animate-pulse rounded bg-slate-200"></div>
          <div class="h-3 w-40 animate-pulse rounded bg-slate-200"></div>
          <div class="ml-auto h-3 w-20 animate-pulse rounded bg-slate-200"></div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div *ngIf="!ticketsLoading && !ticketsError && filteredTickets.length === 0" class="mx-6">
      <app-empty-state
        icon="git-branch"
        title="No linked GitHub issues"
        [description]="query || statusFilter !== 'ALL' ? 'No issues match your current filters.' : 'GitHub issues will appear here once your monitoring system creates them for open findings.'"
      ></app-empty-state>
    </div>

    <!-- Tickets table -->
    <div *ngIf="!ticketsLoading && !ticketsError && filteredTickets.length > 0" class="mx-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-slate-200 bg-slate-50 text-left">
            <th class="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Issue</th>
            <th class="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Resource</th>
            <th class="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Status</th>
            <th class="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Created</th>
            <th class="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Resolved</th>
            <th class="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500"></th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-100">
          <tr
            *ngFor="let ticket of pagedTickets"
            (click)="openDrawer(ticket)"
            class="cursor-pointer transition-colors hover:bg-slate-50"
            [class.bg-violet-50]="selectedTicket === ticket"
          >
            <!-- Issue -->
            <td class="px-5 py-4">
              <div class="flex items-center gap-2">
                <lucide-icon
                  [name]="ticket.status === 'ACTIVE' ? 'circle-dot' : 'circle-check'"
                  [size]="14"
                  [class.text-emerald-500]="ticket.status === 'ACTIVE'"
                  [class.text-slate-400]="ticket.status === 'RESOLVED'"
                  [strokeWidth]="2"
                ></lucide-icon>
                <span class="font-mono text-sm font-medium text-slate-900">#{{ ticket.issueNumber }}</span>
                <span class="max-w-[200px] truncate text-slate-600">{{ ticket.repository }}</span>
              </div>
            </td>
            <!-- Resource -->
            <td class="px-5 py-4">
              <div class="flex flex-col">
                <span class="font-medium text-slate-800">{{ resourceLabel(ticket) }}</span>
                <span *ngIf="ticket.serviceType" class="text-xs text-slate-400">{{ serviceTypeLabel(ticket) }}</span>
              </div>
            </td>
            <!-- Status badge -->
            <td class="px-5 py-4">
              <span
                class="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
                [class.bg-emerald-100]="ticket.status === 'ACTIVE'"
                [class.text-emerald-700]="ticket.status === 'ACTIVE'"
                [class.bg-slate-100]="ticket.status === 'RESOLVED'"
                [class.text-slate-500]="ticket.status === 'RESOLVED'"
              >
                {{ ticket.status === 'ACTIVE' ? 'Open' : 'Resolved' }}
              </span>
            </td>
            <!-- Created -->
            <td class="px-5 py-4 text-slate-400">{{ relativeTime(ticket.createdAt) }}</td>
            <!-- Resolved -->
            <td class="px-5 py-4 text-slate-400">{{ ticket.resolvedAt ? relativeTime(ticket.resolvedAt) : '—' }}</td>
            <!-- Actions -->
            <td class="px-5 py-4">
              <a
                [href]="ticket.issueUrl"
                target="_blank"
                rel="noopener noreferrer"
                (click)="$event.stopPropagation()"
                class="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
              >
                <lucide-icon name="external-link" [size]="11" [strokeWidth]="2"></lucide-icon>
                GitHub
              </a>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div *ngIf="totalPages > 1" class="flex items-center justify-between border-t border-slate-200 px-5 py-3">
        <p class="text-xs text-slate-500">{{ pageStart }}–{{ pageEnd }} of {{ filteredTickets.length }}</p>
        <div class="flex items-center gap-1">
          <button (click)="prevPage()" [disabled]="currentPage === 1"
            class="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40">
            <lucide-icon name="chevron-left" [size]="14" [strokeWidth]="2"></lucide-icon>
          </button>
          <button *ngFor="let p of pageNumbers" (click)="goToPage(p)"
            class="min-w-[28px] rounded px-1.5 py-1 text-xs transition-colors"
            [class.bg-violet-600]="currentPage === p"
            [class.text-white]="currentPage === p"
            [class.text-slate-600]="currentPage !== p"
            [class.hover:bg-slate-100]="currentPage !== p">
            {{ p }}
          </button>
          <button (click)="nextPage()" [disabled]="currentPage === totalPages"
            class="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40">
            <lucide-icon name="chevron-right" [size]="14" [strokeWidth]="2"></lucide-icon>
          </button>
        </div>
      </div>
    </div>

  </ng-container>

</div>

<!-- ── Drawer overlay ───────────────────────────────────────────────────────── -->
<div *ngIf="selectedTicket" class="fixed inset-0 z-40 flex justify-end">

  <!-- Backdrop -->
  <div class="absolute inset-0 bg-slate-900/30 backdrop-blur-[1px]" (click)="closeDrawer()"></div>

  <!-- Drawer panel -->
  <div class="relative z-50 flex h-full w-full max-w-lg flex-col bg-white shadow-2xl">

    <!-- Drawer header -->
    <div class="flex items-start justify-between border-b border-slate-200 px-5 py-4">
      <div class="flex items-center gap-3">
        <div class="flex h-8 w-8 items-center justify-center rounded bg-slate-900">
          <lucide-icon name="github" [size]="16" color="white" [strokeWidth]="2"></lucide-icon>
        </div>
        <div>
          <p class="font-mono text-sm font-semibold text-slate-900">#{{ selectedTicket.issueNumber }}</p>
          <p class="text-xs text-slate-500">{{ selectedTicket.repository }}</p>
        </div>
      </div>
      <div class="flex items-center gap-1">
        <button
          (click)="refreshDrawer()"
          [disabled]="commentsLoading"
          title="Refresh"
          class="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40"
        >
          <lucide-icon name="refresh-cw" [size]="15" [class.animate-spin]="commentsLoading" [strokeWidth]="2"></lucide-icon>
        </button>
        <button (click)="closeDrawer()" class="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700">
          <lucide-icon name="x" [size]="16" [strokeWidth]="2"></lucide-icon>
        </button>
      </div>
    </div>

    <!-- Drawer body -->
    <div class="flex-1 overflow-y-auto px-5 py-5">

      <!-- Status + link row -->
      <div class="mb-5 flex items-center gap-3">
        <span
          class="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold"
          [class.bg-emerald-100]="selectedTicket.status === 'ACTIVE'"
          [class.text-emerald-700]="selectedTicket.status === 'ACTIVE'"
          [class.bg-slate-100]="selectedTicket.status === 'RESOLVED'"
          [class.text-slate-500]="selectedTicket.status === 'RESOLVED'"
        >
          {{ selectedTicket.status === 'ACTIVE' ? 'Open' : 'Resolved' }}
        </span>
        <a
          [href]="selectedTicket.issueUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center gap-1.5 text-xs font-medium text-violet-600 transition-colors hover:text-violet-800"
        >
          <lucide-icon name="external-link" [size]="12" [strokeWidth]="2"></lucide-icon>
          Open in GitHub
        </a>
      </div>

      <!-- Metadata grid -->
      <dl class="mb-6 grid grid-cols-2 gap-x-4 gap-y-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-4 text-sm">
        <div>
          <dt class="text-xs font-medium uppercase tracking-wide text-slate-400">Repository</dt>
          <dd class="mt-0.5 font-medium text-slate-800">{{ selectedTicket.repository }}</dd>
        </div>
        <div>
          <dt class="text-xs font-medium uppercase tracking-wide text-slate-400">Issue #</dt>
          <dd class="mt-0.5 font-mono font-medium text-slate-800">#{{ selectedTicket.issueNumber }}</dd>
        </div>
        <div *ngIf="selectedTicket.resourceName || selectedTicket.resourceId">
          <dt class="text-xs font-medium uppercase tracking-wide text-slate-400">Resource</dt>
          <dd class="mt-0.5 font-medium text-slate-800">{{ resourceLabel(selectedTicket) }}</dd>
        </div>
        <div *ngIf="selectedTicket.serviceType">
          <dt class="text-xs font-medium uppercase tracking-wide text-slate-400">Service</dt>
          <dd class="mt-0.5 font-medium text-slate-800">{{ serviceTypeLabel(selectedTicket) }}</dd>
        </div>
        <div>
          <dt class="text-xs font-medium uppercase tracking-wide text-slate-400">Finding</dt>
          <dd class="mt-0.5 truncate font-mono text-xs text-slate-600">{{ selectedTicket.activeFindingId }}</dd>
        </div>
        <div>
          <dt class="text-xs font-medium uppercase tracking-wide text-slate-400">Created</dt>
          <dd class="mt-0.5 text-slate-600">{{ relativeTime(selectedTicket.createdAt) }}</dd>
        </div>
        <div *ngIf="selectedTicket.resolvedAt">
          <dt class="text-xs font-medium uppercase tracking-wide text-slate-400">Resolved</dt>
          <dd class="mt-0.5 text-slate-600">{{ relativeTime(selectedTicket.resolvedAt) }}</dd>
        </div>
        <div *ngIf="selectedTicket.configId">
          <dt class="text-xs font-medium uppercase tracking-wide text-slate-400">Finding link</dt>
          <dd class="mt-0.5">
            <a
              [routerLink]="['/cloud/' + (selectedTicket.serviceType === 'PIPELINE' ? 'codepipeline' : selectedTicket.serviceType?.toLowerCase() ?? 'lambda')]"
              class="text-xs text-violet-600 hover:text-violet-800"
            >View in monitoring →</a>
          </dd>
        </div>
      </dl>

      <!-- Diagnosis (read-only; never blocks the drawer) -->
      <app-diagnosis-section [view]="enrichment" [loading]="enrichmentLoading"></app-diagnosis-section>

      <!-- Actions -->
      <div class="mb-6">
        <p class="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Actions</p>
        <div class="flex flex-wrap gap-2">
          <a
            [href]="selectedTicket.issueUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
          >
            <lucide-icon name="external-link" [size]="12" [strokeWidth]="2"></lucide-icon>
            View in GitHub
          </a>
          <button
            *ngIf="selectedTicket.status === 'ACTIVE'"
            (click)="setIssueState('closed')"
            [disabled]="actionPending"
            class="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
          >
            <lucide-icon name="circle-x" [size]="12" [strokeWidth]="2"></lucide-icon>
            Close Issue
          </button>
          <button
            *ngIf="selectedTicket.status === 'RESOLVED'"
            (click)="setIssueState('open')"
            [disabled]="actionPending"
            class="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
          >
            <lucide-icon name="circle-check" [size]="12" [strokeWidth]="2"></lucide-icon>
            Re-open Issue
          </button>
        </div>
        <p *ngIf="actionPending" class="mt-2 flex items-center gap-1.5 text-xs text-slate-400">
          <lucide-icon name="loader" [size]="12" class="animate-spin" [strokeWidth]="2"></lucide-icon>
          Working…
        </p>
      </div>

      <!-- Comment history -->
      <div class="mb-6">
        <p class="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Comments</p>
        <!-- Loading skeleton -->
        <div *ngIf="commentsLoading" class="space-y-3">
          <div *ngFor="let _ of [1,2]" class="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
            <div class="mb-2 flex items-center justify-between">
              <div class="h-2.5 w-20 animate-pulse rounded bg-slate-200"></div>
              <div class="h-2.5 w-12 animate-pulse rounded bg-slate-200"></div>
            </div>
            <div class="space-y-1.5">
              <div class="h-2.5 w-full animate-pulse rounded bg-slate-200"></div>
              <div class="h-2.5 w-3/4 animate-pulse rounded bg-slate-200"></div>
            </div>
          </div>
        </div>
        <!-- Error -->
        <p *ngIf="!commentsLoading && commentsError" class="text-xs text-red-500">
          Failed to load comments.
          <button (click)="loadComments()" class="font-medium underline">Retry</button>
        </p>
        <!-- Empty -->
        <p *ngIf="!commentsLoading && !commentsError && comments.length === 0" class="text-xs text-slate-400">
          No comments yet.
        </p>
        <!-- Comment list -->
        <div *ngIf="!commentsLoading && !commentsError && comments.length > 0" class="space-y-3">
          <div *ngFor="let comment of comments" class="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
            <div class="mb-1.5 flex items-center justify-between gap-2">
              <span class="text-xs font-semibold text-slate-700">{{ comment.author }}</span>
              <div class="flex items-center gap-2">
                <span class="text-[11px] text-slate-400">{{ relativeTime(comment.createdAt) }}</span>
                <a
                  *ngIf="comment.url"
                  [href]="comment.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-slate-400 transition-colors hover:text-slate-600"
                >
                  <lucide-icon name="external-link" [size]="11" [strokeWidth]="2"></lucide-icon>
                </a>
              </div>
            </div>
            <p class="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{{ comment.body }}</p>
          </div>
        </div>
      </div>

      <!-- Add comment -->
      <div>
        <p class="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Add comment</p>
        <textarea
          [(ngModel)]="commentBody"
          rows="4"
          placeholder="Leave a comment on this GitHub issue…"
          class="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100"
        ></textarea>
        <button
          (click)="submitComment()"
          [disabled]="!commentBody.trim() || commentPending"
          class="mt-2 flex items-center gap-1.5 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-violet-700 disabled:opacity-50"
        >
          <lucide-icon *ngIf="commentPending" name="loader" [size]="13" class="animate-spin" [strokeWidth]="2"></lucide-icon>
          <lucide-icon *ngIf="!commentPending" name="message-square" [size]="13" [strokeWidth]="2"></lucide-icon>
          {{ commentPending ? 'Posting…' : 'Post Comment' }}
        </button>
      </div>

    </div>
  </div>
</div>
